import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  Download,
  ExternalLink,
  Loader2,
  Maximize,
  PlayCircle,
  RotateCcw,
} from 'lucide-react';

import { ctw } from '@/common/utils/ctw';
import { Button } from '@/components/atoms';

type VideoPlayerProps = {
  videoUrl: string | null;
  websiteUrl?: string;
  className?: string;
};

const PLAYBACK_SPEEDS = [0.5, 1, 1.5, 2] as const;

export const VideoPlayer: FunctionComponent<VideoPlayerProps> = ({
  videoUrl,
  websiteUrl,
  className,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playbackSpeed, setPlaybackSpeed] = useState<(typeof PLAYBACK_SPEEDS)[number]>(1);

  const handleRetry = useCallback(() => {
    if (videoRef.current && videoUrl) {
      setError(null);
      setIsLoading(true);
      videoRef.current.load();
    }
  }, [videoUrl]);

  const handleDownload = useCallback(async () => {
    if (!videoUrl) return;

    try {
      const response = await fetch(videoUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = websiteUrl
        ? `session-recording-${new URL(websiteUrl).hostname}.mp4`
        : 'session-recording.mp4';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(videoUrl, '_blank');
    }
  }, [videoUrl, websiteUrl]);

  const handleFullscreen = useCallback(() => {
    if (videoRef.current?.requestFullscreen) {
      void videoRef.current.requestFullscreen();
    }
  }, []);

  const cyclePlaybackSpeed = useCallback(() => {
    setPlaybackSpeed(current => {
      const currentIndex = PLAYBACK_SPEEDS.indexOf(current);
      const nextIndex = (currentIndex + 1) % PLAYBACK_SPEEDS.length;

      return PLAYBACK_SPEEDS[nextIndex]!;
    });
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  if (!videoUrl) {
    return (
      <div className="flex flex-col items-center justify-center space-y-3 py-16">
        <div className="bg-muted rounded-full p-4">
          <PlayCircle className="text-muted-foreground size-10" />
        </div>
        <p className="text-muted-foreground">No video recording available</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Website context badge */}
      {websiteUrl && (
        <div className="mb-4 flex items-center gap-2">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-sm">
            <ExternalLink className="size-3.5 text-slate-500" />
            <span className="font-medium text-slate-700">{websiteUrl}</span>
          </div>
        </div>
      )}

      {/* Video container */}
      <div className="relative w-full overflow-hidden rounded-xl bg-slate-900 shadow-lg">
        {isLoading && !error && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-slate-900">
            <Loader2 className="size-10 animate-spin text-white" />
            <p className="mt-3 text-sm text-slate-300">Loading video...</p>
          </div>
        )}
        {error ? (
          <div className="flex flex-col items-center justify-center space-y-5 rounded-xl bg-slate-50 py-20">
            <div className="rounded-full bg-red-100 p-4">
              <AlertCircle className="size-8 text-red-500" />
            </div>
            <div className="text-center">
              <p className="font-medium text-slate-900">{error}</p>
              <p className="mt-1 text-sm text-slate-500">
                The video URL may have expired. Try refreshing the page.
              </p>
            </div>
            <Button variant="outline" onClick={handleRetry} className="gap-2">
              <RotateCcw className="size-4" />
              Retry
            </Button>
          </div>
        ) : (
          <video
            ref={videoRef}
            className={ctw('aspect-video w-full', { invisible: isLoading })}
            controls
            preload="metadata"
            onLoadStart={() => {
              setIsLoading(true);
              setError(null);
            }}
            onCanPlay={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setError('Failed to load video');
            }}
          >
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
      </div>

      {/* Controls bar */}
      {!error && (
        <div className="mt-4 flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={cyclePlaybackSpeed}
              className="h-8 min-w-[3.5rem] rounded-full bg-white px-3 text-xs font-semibold shadow-sm hover:bg-slate-100"
            >
              {playbackSpeed}x
            </Button>
            <span className="text-xs text-slate-500">Playback speed</span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleFullscreen}
              className="h-8 gap-1.5 rounded-full bg-white px-3 shadow-sm hover:bg-slate-100"
            >
              <Maximize className="size-3.5" />
              <span className="text-xs">Fullscreen</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              className="h-8 gap-1.5 rounded-full bg-white px-3 shadow-sm hover:bg-slate-100"
            >
              <Download className="size-3.5" />
              <span className="text-xs">Download</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

VideoPlayer.displayName = 'VideoPlayer';
